import { useQueries, useQuery, type QueryClient, type UseQueryResult } from '@tanstack/react-query';

import { apiRequest } from '@/lib/api-client';

import { isRetryableEventFailure } from './constants';
import type { DiscoverResponse, PublicEventDetail } from './types';

/**
 * The slice's whole HTTP surface (`docs/standards/code-organization.md` — `api.ts` is the
 * frontend's `repository.ts`).
 *
 * **Both calls are deliberately unauthenticated-capable.** `apiRequest` attaches a bearer token
 * only when one is in storage, so the same call serves a signed-in member and a signed-out
 * visitor — which is `MEM-019` AC-1, and the reason there is no second "public" client. The
 * server side of that contract is `EDGE_PUBLIC_ROUTES`; a route missing from it answers API
 * Gateway's 401 in a deployed environment however unauthenticated the client is.
 *
 * There is no mutation here and there will not be one. Reserving a seat is `MEM-020`, and
 * saving or favouriting an event is `MEM-014` AC-6's stated absence — a bookmark call added to
 * this file would be the first half of a feature the story says is not built.
 */

const DISCOVER_KEY = ['event-discovery', 'list'] as const;
const eventKey = (eventId: string): readonly unknown[] => ['event-discovery', 'event', eventId];

export function useDiscoverEvents(parameters: {
  readonly search: string;
  readonly atmosphere: string;
}): UseQueryResult<DiscoverResponse> {
  return useQuery({
    queryKey: [...DISCOVER_KEY, parameters.atmosphere, parameters.search],
    queryFn: () =>
      apiRequest<DiscoverResponse>('/api/event-discovery/events', {
        // **An empty atmosphere is "no filter", not a filter that matches nothing.**
        //
        // The API treats `atmosphere` as a term id and an empty one matches no term, so
        // `?atmosphere=` comes back with zero events while `?atmosphere=all` — the sentinel the
        // Discover screen sends — comes back with everything. The marketing page's events band
        // passed the empty string and therefore rendered nothing **whatever was published**,
        // which looked exactly like "there are no events" and was diagnosed as that more than
        // once.
        //
        // Normalised here rather than at the one caller that got it wrong: the next caller will
        // reach for `''` too, and a parameter whose empty value silently means "none" is a trap
        // worth closing at the door.
        searchParams: {
          search: parameters.search,
          atmosphere: parameters.atmosphere === '' ? 'all' : parameters.atmosphere,
        },
      }),
    // The list keeps its previous rows visible while a new query is in flight, so typing does not
    // flash an empty region between keystrokes — and `MEM-014` AC-5's "no events match" copy is
    // then only ever shown for a query that actually came back empty.
    placeholderData: (previous) => previous,
  });
}

/**
 * File an event already read under another of its addresses (`EVT-041` AC-5), so the page that
 * swaps an id for the readable address draws the event it has instead of loading it again.
 */
export function rememberPublicEvent(
  queryClient: QueryClient,
  eventRef: string,
  detail: PublicEventDetail,
): void {
  queryClient.setQueryData(eventKey(eventRef), detail);
}

export function usePublicEvent(eventId: string): UseQueryResult<PublicEventDetail> {
  return useQuery({
    queryKey: eventKey(eventId),
    queryFn: () =>
      apiRequest<PublicEventDetail>(
        `/api/event-discovery/events/${encodeURIComponent(eventId)}`,
      ),
    /*
      A 404 is the answer for a draft, a scheduled event and an event that never existed, and it
      is the same answer on purpose. Retrying it would only delay the message — so a final answer
      is not retried.

      **A transient failure is, twice** (TW 44108653). This was `retry: false` for every failure,
      so one dropped or throttled request — in the burst of reads a reservation triggers, on the
      page a member lands on straight after it — became *"We could not open that event"* until
      they refreshed. The rule for which is which is `isRetryableEventFailure`, shared with the
      page's error state so the two cannot disagree.
    */
    retry: (failureCount, error) => failureCount < 2 && isRetryableEventFailure(error),
  });
}

/**
 * **Several events' public pages at once**, keyed exactly as {@link usePublicEvent} so an event
 * page opened afterwards is already in the cache.
 *
 * For Your Events (`MEM-052`), whose rows need what the member's own registrations and follow-up
 * summaries do not carry — the city, the atmosphere — and which the event's public page already
 * serves to anybody. Reading it here rather than widening the member's own payloads keeps those
 * payloads what their stories vetted, and asks nothing new of the server. A page that fails to
 * load is simply absent from the map, and the row draws what it has.
 */
export function usePublicEvents(
  eventIds: ReadonlyArray<string>,
): ReadonlyMap<string, PublicEventDetail> {
  const ids = [...new Set(eventIds)];
  const results = useQueries({
    queries: ids.map((eventId) => ({
      queryKey: eventKey(eventId),
      queryFn: () =>
        apiRequest<PublicEventDetail>(`/api/event-discovery/events/${encodeURIComponent(eventId)}`),
      retry: false,
    })),
  });
  const found = new Map<string, PublicEventDetail>();
  results.forEach((result, index) => {
    const eventId = ids[index];
    if (eventId !== undefined && result.data !== undefined) found.set(eventId, result.data);
  });
  return found;
}
